import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { authStore } from '../../stores/authStore';
import { AlertTriangle, Loader2, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import SagarMeinYog from '../../assets/sagar_mein_yog.svg';
import { isValidEmail, normalizeEmail } from '../../utils/emailValidation';

// Assets
const dgShippingLogo = "https://data.sagarmeinyog.com//DGMA_logo.png";
import lrqaLogo from "../../assets/lrqa-logo.png";
import authBg from '../../assets/auth-bg.png';
import nusi from '../../assets/nusi.svg';

const RESET_TOKEN_KEY = 'passwordResetToken';

const readStoredResetToken = () => {
  try {
    return sessionStorage.getItem(RESET_TOKEN_KEY);
  } catch {
    return null;
  }
};

const writeStoredResetToken = (token) => {
  try {
    sessionStorage.setItem(RESET_TOKEN_KEY, token);
  } catch {
    // The token still lives in component state for this page load.
  }
};

const clearStoredResetToken = () => {
  try {
    sessionStorage.removeItem(RESET_TOKEN_KEY);
  } catch {
    // Ignore storage failures; the in-memory token is cleared separately.
  }
};

const isExpiredResetError = (message) =>
  typeof message === 'string' &&
  message.toLowerCase().includes('invalid or expired');

const ForgotPassword = () => {
  const { error, loading } = useSnapshot(authStore);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [resetToken, setResetToken] = useState(null);
  const navigate = useNavigate();

  // Keep the token in sessionStorage so a refresh still shows the reset form
  // after the token is removed from the address bar.
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      writeStoredResetToken(tokenFromUrl);
      setResetToken(tokenFromUrl);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('token');
      setSearchParams(newSearchParams, { replace: true });
      return;
    }

    const storedToken = readStoredResetToken();
    if (storedToken) {
      setResetToken(storedToken);
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (resetToken) {
      // Handle password reset with token
      if (!password || !confirmPassword) {
        setLocalError('Please fill in all fields');
        return;
      }
      
      if (password !== confirmPassword) {
        setLocalError('Passwords do not match');
        return;
      }
      
      setLocalError(null);
      
      try {
        await authStore.resetPasswordConfirm(resetToken, password);
        clearStoredResetToken();
        setIsSuccess(true);
        
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
        
      } catch (err) {
        const message = err.message || 'Failed to reset password';
        if (isExpiredResetError(message)) {
          clearStoredResetToken();
          setResetToken(null);
          authStore.error = null;
          setLocalError(
            'This reset link is invalid or has expired. Enter your email to request a new one.',
          );
        } else {
          setLocalError(message);
        }
        console.error('Password reset error:', err);
      }
    } else {
      // Handle forgot password request
      if (!email.trim()) {
        setLocalError('Please enter your email');
        return;
      }
      if (!isValidEmail(email)) {
        setLocalError('Please enter a valid email address');
        return;
      }

      const normalizedEmail = normalizeEmail(email);
      setEmail(normalizedEmail);
      setLocalError(null);

      try {
        await authStore.resetPassword(normalizedEmail);
        setResetSent(true);
      } catch (err) {
        setLocalError(err.message || 'Failed to send reset email');
        console.error('Password reset error:', err);
      }
    }
  };

  // If token is present, show password reset form
  if (resetToken) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - Form */}
        <div className="w-full md:w-1/2 flex flex-col p-8 md:p-12 lg:p-16">
          <div className="flex justify-between items-center mb-8">
            <img src={nusi} alt="NUSI" className="h-16" />
          </div>

          <div className="max-w-md w-full mx-auto my-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {isSuccess ? 'Password Reset Successful' : 'Reset Your Password'}
              </h1>
              <p className="text-gray-600">
                {isSuccess 
                  ? 'Your password has been successfully reset. You can now Login with your new password.'
                  : 'Create a new password for your account.'}
              </p>
            </div>

            {(error || localError) && !isSuccess && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-red-800">Error</h4>
                  <p className="text-sm text-red-700 mt-1">{error || localError}</p>
                </div>
              </div>
            )}

            {isSuccess ? (
              <div className="text-center py-8">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Password Reset Successful</h3>
                <p className="text-gray-600 mb-6">
                  Your password has been successfully reset. Redirecting to login...
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[var(--primary-font)] hover:bg-[var(--secondary-font)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-font)]"
                >
                  Go to Login
                </button>
              </div>
            ) : (
              <form className="space-y-6 w-full" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[var(--primary-font)] focus:border-[var(--primary-font)] sm:text-sm"
                    placeholder="Enter new password"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[var(--primary-font)] focus:border-[var(--primary-font)] sm:text-sm"
                    placeholder="Confirm new password"
                  />
                </div>

                <div className='w-full flex items-center justify-center gap-2'>
                  <Button
                    variant='outline'
                    onClick={() => navigate('/')}
                    className='w-1/2'
                  >
                    Back to Login
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="font-medium w-1/2 justify-center bg-[var(--primary-font)] hover:bg-[var(--secondary-font)] text-white px-6 py-3 rounded-lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Resetting...
                      </>
                    ) : 'Reset Password'}
                  </Button>
                </div>
              </form>
            )}
          </div>

          <div className="mt-auto pt-8">
            <div className="flex justify-between items-center text-xs text-gray-500">
              <div>© {new Date().getFullYear()} DG Shipping. All rights reserved.</div>
              <div className="flex space-x-4">
                <a href="#" className="hover:text-gray-700">Terms</a>
                <a href="#" className="hover:text-gray-700">Privacy</a>
                <a href="#" className="hover:text-gray-700">Help</a>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Image */}
        <div className="hidden md:block md:w-1/2 bg-gray-100 relative">
          <div className={`absolute top-0 left-0 h-full w-full bg-cover bg-centertransition-all duration-500 z-0`} style={{ backgroundImage: `url(${authBg})` }} />
          <div className="absolute inset-0 bg-[var(--primary-font)]/75"></div>
          <div className="relative h-full flex flex-col justify-between p-12 text-white">
            <div className="max-w-md">
              <img src={lrqaLogo} alt="LRQA" className="h-8 mb-6" />
              <h2 className="text-3xl font-bold mb-4">Welcome to DG Shipping LMS</h2>
              <p className="text-blue-100">
                Access your maritime training courses, track your progress, and enhance your professional development.
              </p>
            </div>
            <div className="text-blue-100 text-sm">
              <p>Need help? Contact our support team at</p>
              <a href="mailto:support@dgshipping.com" className="font-medium text-white hover:underline">
                support@dgshipping.com
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Original forgot password form (when no token is present)
  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 flex flex-col p-8 md:p-12 lg:p-16">
        <div className="flex gap-6">
          <img src={nusi} alt="NUSI" className="h-16" />
          <img src={dgShippingLogo} alt="DG Shipping" className="h-16" />
        </div>

        <div className="max-w-md w-full mx-auto my-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Forgot Password</h1>
            <p className="text-gray-600">
              {resetSent 
                ? 'If an account exists with this email, you will receive a password reset link.'
                : 'Enter your email address and we\'ll send you a link to reset your password.'}
            </p>
          </div>

          {(error || localError) && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-red-800">Error</h4>
                <p className="text-sm text-red-700 mt-1">{error || localError}</p>
              </div>
            </div>
          )}

          {resetSent ? (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Check your email</h3>
              <p className="text-gray-600 mb-6">
                We've sent password reset instructions to {email}
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[var(--primary-font)] hover:bg-[var(--secondary-font)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-font)]"
              >
                Back to Login
              </button>
            </div>
          ) : (
            <form className="space-y-4 w-full" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[var(--primary-font)] focus:border-[var(--primary-font)] sm:text-sm"
                  placeholder="Enter your email address"
                />
              </div>

              <div className='w-full flex items-center justify-center gap-2'>
                <Button
                  variant='outline'
                  onClick={() => navigate('/')}
                  className='w-1/2'
                >
                  Back to Login
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="font-medium w-1/2 justify-center bg-[var(--primary-font)] hover:bg-[var(--secondary-font)] text-white px-6 py-3 rounded-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          )}
        </div>

        <div className="mt-auto pt-8">
          <div className="flex justify-between items-center text-xs text-gray-500">
            {/* <div>© {new Date().getFullYear()} DG Shipping. All rights reserved.</div> */}
            <div className="flex space-x-4">
              <a href="#" className="hover:text-gray-700">Terms</a>
              <a href="#" className="hover:text-gray-700">Privacy</a>
              <a href="#" className="hover:text-gray-700">Help</a>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden md:block md:w-1/2 bg-gray-100 relative">
        <div className={`absolute top-0 left-0 h-full w-full bg-cover bg-centertransition-all duration-500 z-0`} style={{ backgroundImage: `url(${authBg})` }} />
        <div className="absolute inset-0 bg-[var(--primary-font)]/75"></div>
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="max-w-md">
            <img src={SagarMeinYog} alt="Sagar Mein Yog" className="h-16 mb-6" />
            <h2 className="text-3xl font-bold mb-4">Welcome to Sagar Mein Yog</h2>
            <p className="text-blue-100">
              Access your maritime training courses, track your progress, and enhance your professional development.
            </p>
          </div>
          <div className="text-blue-100 text-sm">
            <p>Need help? Contact our support team at</p>
            <a href="mailto:support@dgshipping.com" className="font-medium text-white hover:underline">
              support@dgshipping.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
